import { createSelector } from 'reselect'
import { getIn, pickToArray, pickToJS, safeToJS } from 'utils/immutable'
import { isEmptyOrNil } from 'utils/functional'
import { createToJsSelector } from 'selectors/utils'
import { getDescriptionNextIndex } from 'selectors/feature'
import { functionQueriesSelector } from 'selectors/feature/queries'
import { outputPinsSelector } from 'selectors/feature/outputs'

const allFunctionsSelector = getIn(['feature', 'currentFeature', 'functions'])
const featureOutputsSelector = getIn(['feature', 'currentFeature', 'outputs'])

const functionDescriptionSelector = (id) =>
  getIn(['feature', 'currentFeature', 'functions', id, 'description'])
const functionMarkedForDeletionSelector = (id) =>
  getIn(['feature', 'currentFeature', 'functions', id, 'markedForDeletion'])

const functionsIdsSelector = (state, props) =>
  state.getIn(['feature', 'currentFeature', 'feature', props.id, 'functions'])
const getFunctionsIds = createToJsSelector(functionsIdsSelector)

const fnOutputsIdSelector = (state, { id }) =>
  getIn(['feature', 'currentFeature', 'outputMaps', id, 'outputs'], state)

const functionMappedOutputsSelector = createSelector(
  fnOutputsIdSelector,
  featureOutputsSelector,
  pickToArray
)

const getFunctionMappedOutputs = createToJsSelector(
  functionMappedOutputsSelector
)

const getFunctionQueries = (id) =>
  createToJsSelector(functionQueriesSelector(id))

// TODO: THIS IS NEARLY IDENTICAL TO selectors/feature/outputs/getFunctionOutput
const getFunctionOutputs = createSelector(
  createToJsSelector(functionMappedOutputsSelector),
  createToJsSelector(outputPinsSelector),
  (functionOutputs, outputPins) => {
    if (isEmptyOrNil(functionOutputs) || isEmptyOrNil(outputPins)) return null
    return functionOutputs.map((featureOutput) => {
      const selectedOutput = outputPins[featureOutput.mappedOutputId]
      if (selectedOutput) {
        selectedOutput.outputDescription = selectedOutput.description
      }
      const isOutputBool = selectedOutput
        ? selectedOutput.isOutputBoolean
        : null
      const mergedOutput = { ...selectedOutput, ...featureOutput }
      const { polarity = 'AH', current = 2 } = mergedOutput || {}
      return {
        ...mergedOutput,
        isOutputBoolean: isOutputBool,
        polarity,
        current,
      }
    })
  }
)

const makeGetFunctionOutputs = () => getFunctionOutputs

const getNewFunctionIndex = createSelector(
  allFunctionsSelector,
  getDescriptionNextIndex
)

const getFunctionDescription = (fnId) =>
  createSelector(
    functionDescriptionSelector(fnId),
    (description) => description
  )

const getFunctionMarkedForDeletion = (fnId) =>
  createSelector(functionMarkedForDeletionSelector(fnId), (mark) => mark)

const getFunctionLinks = createSelector(allFunctionsSelector, (allFunctions) =>
  allFunctions
    .valueSeq()
    .map((fn) => pickToJS(['uri', 'description'], fn))
    .sort((a, b) =>
      a.description.localeCompare(b.description, undefined, { numeric: true })
    )
    .toJS()
)

const getFnOutputsIdsSelector = (fnId) =>
  createToJsSelector(fnOutputsIdSelector(fnId))

export const getFunctionOutputsIds = createSelector(
  fnOutputsIdSelector,
  safeToJS
)

export {
  getFunctionsIds,
  getFunctionMappedOutputs,
  getFunctionOutputs,
  makeGetFunctionOutputs,
  getFunctionQueries,
  getFnOutputsIdsSelector,
  getFunctionDescription,
  getFunctionMarkedForDeletion,
  getNewFunctionIndex,
  getFunctionLinks,
  functionsIdsSelector,
  allFunctionsSelector,
}
